import { CommonModule } from '@angular/common';
import { LocalizationModule } from '@abp/ng.core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { DwApiSelectDirective, DwApiSelectObservable } from '@digiwall/core';
import { AutoSizedDropdownDirective } from '@digiwall/ui-kit';
import { DwActiveLookupRequestDto, DwLookupRequestDto } from '@digiwall/goliath-core/proxy';
import { WorkOrderDayBookDto } from '@digiwall/goliath-documents/proxy';
import {
  PRODUCTION_STEP_DROPDOWN_KEYS,
  PRODUCTION_STEP_TEXT_KEYS,
  ProductionStepFilterOptionsService,
  ProductionStepPlanningFilterKey,
  ProductionStepPlanningFilterKeys,
} from '../../../planning-common/filters';
import {
  ApiDropdown,
  BaseBoardFilterComponent,
} from '../../board-filter/base-board-filter.component';
import { ProductionStepPlanningItemFieldsDto } from './production-step-planning-item-fields';

/**
 * Planner board filter for the ProductionStep data source. Supplies this source's filter keys, their
 * labels, field extraction and option-label resolution to the shared {@link BaseBoardFilterComponent},
 * which owns all of the rendering + state + predicate plumbing.
 *
 * DayBook renders as a static option list; Customer / Manufacturer / Equipment / Worker render as lazy
 * `dwApiSelect` lookups (labels resolved natively, results constrained to the configured presets when
 * ≥2 are set); Description / DisplayNumber / StepName are client-side free-text inputs.
 */
@Component({
  selector: 'document-production-step-board-filter',
  templateUrl: '../../board-filter/base-board-filter.component.html',
  styleUrl: '../../board-filter/base-board-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    LocalizationModule,
    NgSelectModule,
    AutoSizedDropdownDirective,
    DwApiSelectDirective,
  ],
})
export class ProductionStepBoardFilterComponent extends BaseBoardFilterComponent {
  private readonly options = inject(ProductionStepFilterOptionsService);

  protected readonly keys = ProductionStepPlanningFilterKeys;
  protected readonly dropdownKeys: readonly ProductionStepPlanningFilterKey[] =
    PRODUCTION_STEP_DROPDOWN_KEYS;
  protected readonly textKeys: readonly ProductionStepPlanningFilterKey[] =
    PRODUCTION_STEP_TEXT_KEYS;

  protected readonly labelKey: Record<ProductionStepPlanningFilterKey, string> = {
    [this.keys.dayBook]: 'Doc::DayBook',
    [this.keys.customer]: '::Customer',
    [this.keys.manufacturer]: '::Manufacturer',
    [this.keys.equipment]: 'Product::Equipment:Equipment',
    [this.keys.worker]: 'Contact::Worker',
    [this.keys.description]: '::Description',
    [this.keys.displayNumber]: '::Number',
    [this.keys.stepName]: 'Doc::ProductionFolderLine:Step',
  };

  private readonly dayBooks = signal<WorkOrderDayBookDto[]>([]);

  // Lazy lookups; the loader constrains results to the configured presets.
  private readonly customerSelect: DwApiSelectObservable = dto => {
    this.applyPresetIds(this.keys.customer, dto);
    return this.options.customerSelect(dto as DwActiveLookupRequestDto<string>);
  };

  private readonly manufacturerSelect: DwApiSelectObservable = dto => {
    this.applyPresetIds(this.keys.manufacturer, dto);
    return this.options.manufacturerSelect(dto as DwActiveLookupRequestDto<string>);
  };

  private readonly equipmentSelect: DwApiSelectObservable = dto => {
    this.applyPresetIds(this.keys.equipment, dto);
    return this.options.equipmentSelect(dto as DwLookupRequestDto<string>);
  };

  private readonly workerSelect: DwApiSelectObservable = dto => {
    this.applyPresetIds(this.keys.worker, dto);
    return this.options.workerSelect(dto as DwActiveLookupRequestDto<string>);
  };

  protected override readonly apiDropdowns: Record<string, ApiDropdown> = {
    [this.keys.customer]: { apiSelect: this.customerSelect, bindLabel: 'name' },
    [this.keys.manufacturer]: { apiSelect: this.manufacturerSelect, bindLabel: 'code' },
    [this.keys.equipment]: { apiSelect: this.equipmentSelect, bindLabel: 'name' },
    [this.keys.worker]: { apiSelect: this.workerSelect, bindLabel: 'code' },
  };

  constructor() {
    super();
    this.options
      .loadDayBooks()
      .pipe(takeUntilDestroyed())
      .subscribe(dayBooks => this.dayBooks.set(dayBooks));
  }

  // ── Static option-label resolution (DayBook) ────────────────────────────────────

  private readonly dayBookLabels = computed(
    () => new Map(this.dayBooks().map(d => [d.id ?? '', d.name?.current ?? d.id ?? ''])),
  );

  protected fieldValue(key: string, displayFields: unknown): string | undefined {
    const fields = (displayFields ?? {}) as ProductionStepPlanningItemFieldsDto;
    switch (key) {
      case this.keys.dayBook:
        return fields.dayBookId;
      case this.keys.customer:
        return fields.customerId;
      case this.keys.manufacturer:
        return fields.manufacturerId;
      case this.keys.equipment:
        return fields.equipmentId;
      case this.keys.worker:
        return fields.workerId;
      case this.keys.description:
        return fields.description;
      case this.keys.displayNumber:
        return fields.displayNumber;
      case this.keys.stepName:
        return fields.stepName;
      default:
        return undefined;
    }
  }

  protected optionLabel(key: string, id: string): string {
    switch (key) {
      case this.keys.dayBook:
        return this.dayBookLabels().get(id) ?? id;
      default:
        return id; // Customer / Manufacturer / Equipment / Worker are api-select (labels resolved by the lookup).
    }
  }
}
